import type { PluginListenerHandle, PushNotification } from "@capacitor/core";
import { Capacitor } from "@capacitor/core";
import wait from "waait";
import { PushNotificationData } from "./types";

declare module "@capacitor/core" {
  // https://capacitorjs.com/docs/apis/push-notifications#notificationpermissionresponse
  interface NotificationPermissionResponse {
    granted: boolean;
  }

  interface PushNotificationsPlugin {
    // https://capacitorjs.com/docs/apis/push-notifications#requestpermission
    requestPermission: () => Promise<NotificationPermissionResponse>;
    addListener(
      eventName: PushNotificationEvent,
      listenerFunc: (...args: unknown[]) => void
    ): PluginListenerHandle;
  }
}

export type PushNotificationEvent =
  | "pushNotificationActionPerformed"
  | "pushNotificationReceived"
  | "registrationError"
  | "registration";

export const extractPushNotificationData = (
  notification: PushNotification
): PushNotificationData => {
  switch (Capacitor.getPlatform()) {
    case "ios":
      return notification?.data?.data;
    case "android":
      return notification?.data;
    default:
      return null;
  }
};

// If the app was opened by an interaction with a push notification, Capacitor
// will emit it under pushNotificationActionPerformed provided you listen early enough.
// This closure listens for that initial notification event and returns an accessor
export const getAppEntryNotificationData = (() => {
  let notificationData: PushNotificationData = null;

  if (Capacitor.isPluginAvailable("PushNotifications")) {
    const listener = Capacitor.Plugins.PushNotifications.addListener(
      "pushNotificationActionPerformed",
      (action) => {
        notificationData = extractPushNotificationData(action.notification);
        // Remove the listener after we receive the initial notification.
        // Future notifications will be handled by PushNotificationsContext
        listener.remove();
      }
    );

    // Remove the listener if there isn't an initial notification within 5 seconds
    void wait(5000).then(listener.remove);
  }

  return () => notificationData;
})();
